import type { FC, ReactElement } from "react"

import { AnimatePresence, m, useReducedMotion } from "framer-motion"
import eq from "lodash-es/eq"
import includes from "lodash-es/includes"
import isEmpty from "lodash-es/isEmpty"
import isNull from "lodash-es/isNull"

import {
    ESpecialChar,
    ETypographyAlignment,
    ETypographyColor,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    InfoBox,
    SpecialChars,
    Typography
} from "@/3514/components"
import { type TUseInteractionState, useInteractionState } from "@/3514/hooks"
import { EFeedbackModalType, EFeedbackModalVariant } from "@/3514/modals"
import { type TProductTypeQuery, useProductTypeQuery } from "@/3514/queries"
import { buildCssClassName } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"
import type { TEmptyCallback } from "@/shared/types/functions"

import { CoachingChatError2Icon as ErrorIconAiStuck } from "../assets"
import { coachingChatUiConfig as uiConfig } from "../config"
import { useCoachingChatContext as useChatContext } from "../context"
import {
    useCoachingChatConversationErrorSelector as useConversationErrorSelector,
    useCoachingChatConversationStatusSelector as useConversationStatusSelector
} from "../store"
import { coachingChatStyles as chatStyles } from "../styles"
import {
    ECoachingChatConversationStatus as EConversationStatus,
    ECoachingChatConversationErrorVariant as EErrorVariant,
    type TCoachingChatConversationError as TConversationError
} from "../types"

import { CoachingChatReportFeedbackOpener as ReportFeedbackOpener } from "./coaching-chat.report-feedback-opener.component"

type TProps = {
    variant?: EErrorVariant
    isNotInteractive?: boolean
}

const { errorBoxTexts: texts } = uiConfig

const {
    conversation: { errorBox: classes }
} = chatStyles

const Error: FC<TProps> = ({
    variant: incomingErrorVariant = undefined,
    isNotInteractive = false
}: TProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const isMotionReduced: boolean = useReducedMotion()

    const { data: productType }: TProductTypeQuery = useProductTypeQuery()

    const {
        entityId,
        conversation: { feedRef, feedScrollerHook },
        feedback: { handleModalOpen }
    }: ReturnType<typeof useChatContext> = useChatContext()

    const { variant: chatErrorVariant, payload: chatErrorPayload }: TConversationError =
        useConversationErrorSelector(entityId)

    const conversationStatus: EConversationStatus = useConversationStatusSelector(entityId)

    const scrollFeedToBottom: TEmptyCallback = feedScrollerHook(feedRef)

    const shouldBeVisibleBasedOnStatus: boolean = !includes<EConversationStatus>(
        [EConversationStatus.NotAvailable, EConversationStatus.Locked, EConversationStatus.ReadyToStart],
        conversationStatus
    )

    const hasError: boolean = !isNull(chatErrorVariant)

    const errorVariant: EErrorVariant = isEmpty(incomingErrorVariant) ? chatErrorVariant : EErrorVariant.Api

    const isViolationError: boolean = eq(errorVariant, EErrorVariant.Violation)
    const isAiStuckError: boolean = eq(errorVariant, EErrorVariant.AiStuck)
    const isApiError: boolean = eq(errorVariant, EErrorVariant.Api) || eq(errorVariant, EErrorVariant.ApiFailure)

    const {
        actions: { onMouseEnter, onMouseLeave, onBlur, onFocus },
        isActionAffected
    }: TUseInteractionState = useInteractionState()

    function handleAction(): void {
        return isAiStuckError
            ? (isEmpty(productType?.supportEmail) ? void 0 : window.open(`mailto:${productType.supportEmail}`, "_self"),
              void 0)
            : handleModalOpen({
                  variant: EFeedbackModalVariant.ChatApiError,
                  type: EFeedbackModalType.GeneralExtended,
                  payload: {
                      renderDetail: (): ReactElement => (
                          <InfoBox
                              variant="warning"
                              title={t(texts.title(EErrorVariant.Api))}
                              message={t(texts.description(EErrorVariant.Api))}
                          />
                      )
                  },
                  modalTitle: "entities.coachingChat.feedbackModal.apiError.title"
              })
    }

    return (
        <AnimatePresence initial={false} mode="wait" presenceAffectsLayout>
            {hasError && shouldBeVisibleBasedOnStatus && (
                <m.div
                    layout
                    initial={{ opacity: 0, scale: 0.7 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.7 }}
                    transition={{ duration: isMotionReduced ? 0 : 0.25, ease: [0.4, 0, 0.2, 1] }}
                    onAnimationStart={(): void => void scrollFeedToBottom()}
                >
                    <div className={classes.container(errorVariant)}>
                        {includes<EErrorVariant>(
                            [EErrorVariant.Api, EErrorVariant.ApiFailure, EErrorVariant.Violation],
                            errorVariant
                        ) ? (
                            <InfoBox
                                variant="warning"
                                title={t(texts.title(errorVariant))}
                                message={isApiError ? t(texts.description(errorVariant)) : chatErrorPayload.message}
                                {...(isNotInteractive || isViolationError
                                    ? {}
                                    : {
                                          action: {
                                              onClick: handleAction,
                                              children: t(texts.action(errorVariant)),
                                              textWeight: ETypographyWeight.Bold
                                          }
                                      })}
                                {...(isViolationError && {
                                    content: (
                                        <ReportFeedbackOpener
                                            variant="conversation"
                                            moderationId={chatErrorPayload.moderationId}
                                            modalVariant={EFeedbackModalVariant.ChatViolation}
                                        />
                                    )
                                })}
                            />
                        ) : null}

                        {includes<EErrorVariant>([EErrorVariant.AiStuck], errorVariant) ? (
                            <div className={classes.textWrapper(errorVariant)}>
                                <div className={classes.titleWrapper(errorVariant)}>
                                    <ErrorIconAiStuck />

                                    <Typography
                                        className={classes.text()}
                                        alignment={ETypographyAlignment.Center}
                                        color={ETypographyColor.Black}
                                        size={ETypographySize.Small}
                                        tag={ETypographyTag.Paragraph}
                                        text={t(texts.title(errorVariant))}
                                        weight={ETypographyWeight.Bold}
                                    />
                                </div>

                                <div>
                                    <Typography
                                        className={classes.text()}
                                        alignment={ETypographyAlignment.Center}
                                        size={ETypographySize.Small}
                                        tag={ETypographyTag.Span}
                                        text={t(texts.description(errorVariant))}
                                    />

                                    {SpecialChars[ESpecialChar.EmptySpace]}

                                    <div
                                        role="link"
                                        tabIndex={0}
                                        className={buildCssClassName(classes.text(), classes.textActionWrapper())}
                                        onClick={handleAction}
                                        onKeyDown={handleAction}
                                        onBlur={onBlur}
                                        onFocus={onFocus}
                                        onMouseEnter={onMouseEnter}
                                        onMouseLeave={onMouseLeave}
                                    >
                                        <Typography
                                            className={buildCssClassName(
                                                classes.text(),
                                                classes.textAction(isActionAffected)
                                            )}
                                            size={ETypographySize.Small}
                                            weight={
                                                isActionAffected ? ETypographyWeight.Bold : ETypographyWeight.Semibold
                                            }
                                            tag={ETypographyTag.Span}
                                            text={t(texts.action(errorVariant))}
                                            color={ETypographyColor.Accent}
                                        />
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </m.div>
            )}
        </AnimatePresence>
    )
}

Error.displayName = "CoachingChatError"

export { Error as CoachingChatError }
